'use client';

import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import NextLink from 'next/link';
import { useEffect, useState } from 'react';
import { ROUTES } from '../../../constants/routes';
import { cn } from '../../../lib/utils';
import { themeConfig } from '../../../theme';
import { Button } from '../../../components/ui/Button';
import { buttonVariants } from '../../../components/ui/button-variants';
import { IconButton } from '../../../components/ui/icon-button';
import {
  landingMobileNavLabel,
  landingNavCtas,
  landingNavItems,
} from '../content/landing-copy';

const desktopNavLinkClass =
  'rounded-md px-3 py-2 text-sm font-medium text-header-muted transition-colors hover:text-header-foreground';

export function MarketingNav() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <header className='sticky top-0 z-40 w-full border-b border-neutral-200/80 bg-header text-header-foreground'>
      <LandingNavBar open={open} onOpenChange={setOpen} />
      {open ? (
        <MobileNavDrawer onClose={() => setOpen(false)} />
      ) : null}
    </header>
  );
}

function LandingNavBar({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <div className='mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4 px-4 sm:h-[4.75rem] sm:px-6 lg:px-8'>
      <NextLink
        href={ROUTES.home}
        className='flex min-w-0 shrink-0 items-center rounded-lg outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- static brand asset */}
        <img
          src={themeConfig.logos.brandingHorizontal}
          alt={`${themeConfig.brand.shortName} logo`}
          width={182}
          height={81}
          className='h-9 w-auto max-w-[min(100%,11.5rem)] object-contain object-left sm:h-11 sm:max-w-[11.5rem]'
        />
      </NextLink>

      <nav
        className='hidden items-center gap-1 lg:flex'
        aria-label='Marketing navigation'
      >
        {landingNavItems.map((item) => (
          <NextLink
            key={item.label}
            href={item.href}
            className={desktopNavLinkClass}
          >
            {item.label}
          </NextLink>
        ))}
      </nav>

      <div className='hidden items-center gap-3 lg:flex'>
        <NextLink
          href={landingNavCtas.logIn.href}
          className='rounded-md px-3 py-2 text-sm font-medium text-header-foreground transition-colors hover:text-primary-600'
        >
          {landingNavCtas.logIn.label}
        </NextLink>
        <NextLink
          href={landingNavCtas.primary.href}
          className={buttonVariants({
            variant: 'primary',
            size: 'md',
            className: 'min-h-11 rounded-xl px-5',
          })}
        >
          {landingNavCtas.primary.label}
        </NextLink>
      </div>

      <IconButton
        type='button'
        variant='ghost'
        className='rounded-lg p-2 text-header-foreground lg:hidden [&_svg]:size-7'
        aria-expanded={open}
        aria-controls={open ? 'marketing-mobile-nav' : undefined}
        aria-label={open ? 'Close menu' : 'Open menu'}
        onClick={() => onOpenChange(!open)}
      >
        {open ? (
          <XMarkIcon className='size-7' aria-hidden />
        ) : (
          <Bars3Icon className='size-7' aria-hidden />
        )}
      </IconButton>
    </div>
  );
}

function MobileNavDrawer({ onClose }: { onClose: () => void }) {
  return (
    <div
      id='marketing-mobile-nav'
      className='fixed inset-0 z-50 lg:hidden'
      role='dialog'
      aria-modal='true'
      aria-label='Site menu'
    >
      <Button
        type='button'
        variant='ghost'
        className='absolute inset-0 h-full min-h-0 w-full rounded-none border-0 bg-foreground/50 p-0 shadow-none hover:bg-foreground/55'
        aria-label='Close menu'
        onClick={onClose}
      />
      <div className='absolute right-0 top-0 flex h-full w-[min(100%,20rem)] flex-col border-l border-border bg-card text-card-foreground shadow-xl'>
        <div className='flex h-16 shrink-0 items-center justify-between border-b border-border px-4'>
          <span className='font-heading text-sm font-semibold'>
            {landingMobileNavLabel}
          </span>
          <IconButton
            type='button'
            variant='ghost'
            className='rounded-lg text-muted-foreground hover:text-foreground [&_svg]:size-6'
            aria-label='Close menu'
            onClick={onClose}
          >
            <XMarkIcon className='size-6' aria-hidden />
          </IconButton>
        </div>
        <nav
          className='flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-y-contain p-3'
          aria-label='Mobile navigation'
        >
          {landingNavItems.map((item) => (
            <NextLink
              key={item.label}
              href={item.href}
              className={cn(
                'min-h-11 rounded-lg px-3 py-3 text-base font-medium text-foreground hover:bg-accent',
              )}
              onClick={onClose}
            >
              {item.label}
            </NextLink>
          ))}
          <NextLink
            href={landingNavCtas.logIn.href}
            className='min-h-11 rounded-lg px-3 py-3 text-base font-medium text-foreground hover:bg-accent'
            onClick={onClose}
          >
            {landingNavCtas.logIn.label}
          </NextLink>
          <NextLink
            href={landingNavCtas.primary.href}
            className='mt-2 inline-flex min-h-11 items-center justify-center rounded-xl bg-primary-600 px-4 py-3 text-center text-base font-medium text-primary-foreground hover:bg-primary-700'
            onClick={onClose}
          >
            {landingNavCtas.primary.label}
          </NextLink>
        </nav>
      </div>
    </div>
  );
}
